import React, { useContext, useEffect, useState } from 'react';
import { CsvContext } from '../provider/CSVProvider';
import DropdownMenu from '../components/DropdownMenu';
import InputField from '../components/InputField';
import SubmitButton from '../components/SubmitButton';
import CalculationsChart from '../components/CalculationsChart';
import CalculationsCard from '../components/CalculationsCard';
import ResetButton from '../components/ResetButton';
import BestOptionsChart from '../components/BestOptionsChart';
import DescriptionsCard from '../components/DescriptionsCard';
import RateButtons from '../components/RateButtons';
import TotalIRRDisplay from '../components/TotalIRRDisplay';

const RilaCalculator = () => {
  
  const { optionsData, segmentsData, indicesData } = useContext(CsvContext);
  const [filters,setFilters] = useState({
    index: "",
    yearsInvested: "",
    principalAmount: "",
    annualRate: ""
  });
  const [chartData, setChartData] = useState(null);
  const [hoveredData, setHoveredData] = useState(null);
  const [indexValues, setIndexValues] = useState([]);
  const [bestOptions, setBestOptions] = useState([]);
  const [selectedRateIndex, setSelectedRateIndex] = useState(null);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [segments, setSegments] = useState([]);
  const [indices, setIndices] = useState([]);
  const [optionsMatchingIndex, setOptionsMatchingIndex] = useState([]);
  const [optionsMatchingYears, setOptionsMatchingYears] = useState([]);
  const [principalAmountMinimum, setPrincipalAmountMinimum] = useState(0);
  const annualRateMinimum = -100;

  const displayIRR = filters.yearsInvested !== "" && filters.annualRate !== "" && parseInt(filters.yearsInvested) > 1 && parseFloat(filters.annualRate) >= -100;

  useEffect(() => {
    setOptionsMatchingIndex(optionsData.filter(option => ((filters.index === "" || option.Index === filters.index))));
    setOptionsMatchingYears(optionsData.filter(option => ((filters.yearsInvested === "" || option.Years === filters.yearsInvested))));
    setPrincipalAmountMinimum(Math.min(...optionsData.filter(option => ((filters.index === "" || option.Index === filters.index) && 
        (filters.yearsInvested === "" || option.Years === filters.yearsInvested)))
      .map(option => parseFloat(option.Minimum.replace(',','')))));
  },[optionsData,filters.index,filters.yearsInvested]);

  useEffect(() => {
    if (bestOptions.length !== 0) {

      const optionRateValues = bestOptions[selectedOptionIndex].rateValues[selectedRateIndex];
      const indexRateValues = indexValues[selectedRateIndex];

      setChartData({
        labels: optionRateValues.values.map(value => [value.month, value.year]),
        datasets: [
          {
            data: optionRateValues.values.map(value => value.amount),
            rates: optionRateValues.values.map(value => value.rate),
            borderColor: "blue",
            backgroundColor: "blue"
          },
          {
            data: indexRateValues.values.map(value => value.amount),
            rates: indexRateValues.values.map(value => value.rate),
            borderColor: "orange",
            backgroundColor: "orange"
          }
        ]
      });

      setHoveredData({
        segmentValue: optionRateValues.finalAmount,
        segmentRate: optionRateValues.finalRate,
        indexValue: indexRateValues.finalAmount,
        indexRate: indexRateValues.finalRate,
        label: [optionRateValues.finalMonth, optionRateValues.finalYear]
      });
    }
  },[bestOptions,indexValues,selectedRateIndex,selectedOptionIndex])

  const resetFilters = () => {
    setFilters({...filters, 
      index: "",
      yearsInvested: "",
      principalAmount: "",
      annualRate: ""});
  };

  const handleSubmit = (bestOptions,indexValues,inputRateIndex) => {
    setBestOptions(bestOptions);
    setIndexValues(indexValues);
    setSelectedRateIndex(inputRateIndex);
    setSelectedOptionIndex(0);

    const providerSegmentTypes = bestOptions.map(bestOption => (bestOption.option.Provider + '|' + bestOption.option.SegmentType));
    setSegments(segmentsData.filter(item => (providerSegmentTypes.includes(item.Provider + '|' + item.SegmentType))));

    const indexTypes = bestOptions.map(bestOption => (bestOption.option.Index));
    setIndices(indicesData.filter(item => indexTypes.includes(item.Index)));
  };

  const handleChartHover = (segmentValue, segmentRate, indexValue, indexRate, label) => {
    setHoveredData((prevData) => {
      if (prevData !== null && prevData.segmentValue.equals(segmentValue)
        && prevData.segmentRate.equals(segmentRate) && prevData.indexValue.equals(indexValue) 
        && prevData.indexRate.equals(indexRate) && prevData.label === label) {
        return prevData;
      } else {
        return {segmentValue: segmentValue, segmentRate: segmentRate, 
          indexValue: indexValue, indexRate: indexRate, label: label};
      }
    });
  };

  const handleRateSelect = (index) => {
    const curOptionSeleted = bestOptions[selectedOptionIndex];
    bestOptions.sort((a,b) => b.rateValues[index].finalAmount.comparedTo(a.rateValues[index].finalAmount));
    setSelectedOptionIndex(bestOptions.findIndex(option => option === curOptionSeleted));
    setSelectedRateIndex(index);
  };

  return (
    <>
      <div className="row">
        <div className="col s12 l3">
          <DropdownMenu name='Index' field='Index' filterValue={filters.index} options={optionsMatchingYears} onSelect={value => setFilters({...filters, index: value})} required={false}/>
          <DropdownMenu name='Years Invested' field='Years' filterValue={filters.yearsInvested} options={optionsMatchingIndex} onSelect={value => setFilters({...filters, yearsInvested: value})} required={true}/>
          <InputField name='Principal Amount' filterValue={filters.principalAmount} valueMinimum={principalAmountMinimum} onSelect={value => setFilters({...filters, principalAmount: value})} required={true}/>
          <InputField name='Index Performance' filterValue={filters.annualRate} valueMinimum={annualRateMinimum} onSelect={value => setFilters({...filters, annualRate: value})} required={true}/>
          {displayIRR && <TotalIRRDisplay years={filters.yearsInvested} annualRate={filters.annualRate} />}
          <div className='row'>
            <div className='col s6'>
              <ResetButton filters={filters} onSubmit={resetFilters}/>
            </div>
            <div className='col s6'>
              <SubmitButton filters={filters} principalAmountMinimum={principalAmountMinimum} annualRateMinimum={annualRateMinimum} onSubmit={handleSubmit} />
            </div>
          </div>
        </div>
        <div className="col s12 l9">
          <div className='row'>
            <div className='col s12 l8'>
              <div className='row'>
                <div className='col s12'>
                  {bestOptions.length !== 0 && <RateButtons selectedOption={bestOptions[selectedOptionIndex]} rateIndex={selectedRateIndex} onSelect={handleRateSelect} />}
                </div>
              </div>
              <div className='row'>
                <div className='col s12'>
                  {chartData && <CalculationsChart chartData={chartData} onHover={handleChartHover} />}
                </div>
              </div>
            </div>
            <div className='col s12 l4'>
              {hoveredData && <CalculationsCard hoveredData={hoveredData} />}
            </div>
          </div>
        </div>
      </div>

      <div className='row'>
        <div className='col s12'>
          {bestOptions.length !== 0 && 
            <BestOptionsChart bestOptions={bestOptions} indexValues={indexValues} rowIndex={selectedOptionIndex} rateIndex={selectedRateIndex} segments={segments} onClick={index => setSelectedOptionIndex(index)} />}
        </div>
      </div>

      <div className='row'>
        <div className='col s12 l6'>
          {indices.length !== 0 && <DescriptionsCard title='Indices' items={indices}/>}
        </div>
        <div className='col s12 l6'>
          {segments.length !== 0 && <DescriptionsCard title='Segments' items={segments}/>}
        </div>
      </div>

    </>
  );
};

export default RilaCalculator;